import React, { useEffect, useState } from "react";
import { Trophy, Flame, Medal, Search, Gift, Coins } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReferralLeaderboardHook from "@/components/ReferralLeaderboardHook";

const MEDAL_COLORS = { 1: "text-[#FBBF24]", 2: "text-slate-300", 3: "text-indigo-400" };

export default function LeaderboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [country, setCountry] = useState("");
  const [track, setTrack] = useState("data_cloud");
  const [viewMode, setViewMode] = useState("xp"); // xp | referrals
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  const load = (t, p, c) => {
    setLoading(true);
    if (t === "sfmc") {
      api.get("/sfmc/leaderboard").then((res) => setData(res.data)).finally(() => setLoading(false));
      return;
    }
    if (t === "mc_next") {
      api.get("/mc-next/leaderboard").then((res) => setData(res.data)).finally(() => setLoading(false));
      return;
    }
    const params = { period: p };
    if (c.trim()) params.country = c.trim();
    api.get("/leaderboard", { params }).then((res) => setData(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (viewMode !== "xp") return;
    load(track, period, country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, track, viewMode]);

  useEffect(() => {
    if (viewMode !== "referrals" || referralData) return;
    setReferralLoading(true);
    api.get("/referrals/leaderboard").then((res) => setReferralData(res.data)).finally(() => setReferralLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const handleCountrySearch = (e) => {
    e.preventDefault();
    load(track, period, country);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="leaderboard-page">
      <div className="text-center mb-6">
        <Trophy className="w-8 h-8 text-[#FBBF24] mx-auto mb-2" />
        <h1 className="font-heading text-3xl font-semibold text-white">Global Leaderboard</h1>
        {data && <p className="text-slate-400 text-sm mt-1">Your rank: <span className="text-indigo-400 font-semibold">#{data.my_rank}</span></p>}
      </div>

      <ReferralLeaderboardHook />

      <div className="flex justify-center mb-6">
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="bg-white/5 p-1">
            <TabsTrigger value="xp" data-testid="leaderboard-viewmode-xp" className="data-[state=active]:bg-primary data-[state=active]:text-white">XP Leaderboard</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="leaderboard-viewmode-referrals" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Referral Leaderboard</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {viewMode === "referrals" ? (
        <div data-testid="referral-leaderboard-view">
          <p className="text-xs text-slate-400 text-center mb-4" data-testid="referral-leaderboard-hint">
            All-time ranking by friends referred who completed Day 1. Top 3 win a fresh bonus-coin prize every Monday.
          </p>
          {referralLoading || !referralData ? (
            <div className="flex items-center justify-center py-20" data-testid="referral-leaderboard-loading">
              <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="glass-panel rounded-2xl overflow-hidden">
              {referralData.leaderboard.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-10">No completed referrals yet — be the first!</p>
              )}
              {referralData.leaderboard.map((u) => (
                <div
                  key={`${u.rank}-${u.name}`} data-testid={`referral-leaderboard-row-${u.rank}`}
                  className="flex items-center gap-4 px-5 py-3.5 border-b border-white/5 last:border-0"
                >
                  <div className="w-8 flex justify-center">
                    {u.rank <= 3 ? <Medal className={`w-5 h-5 ${MEDAL_COLORS[u.rank]}`} /> : <span className="text-sm font-bold text-slate-400">#{u.rank}</span>}
                  </div>
                  <div className="w-9 h-9 rounded-full bg-amber-400/20 flex items-center justify-center text-sm font-semibold text-white overflow-hidden">
                    {u.picture ? <img src={u.picture} alt={u.name} loading="lazy" className="w-full h-full object-cover" /> : u.name?.[0]}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.level_title}{u.country ? ` · ${u.country}` : ""}</p>
                  </div>
                  {u.rank <= 3 && (
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-400" data-testid={`referral-weekly-bonus-badge-${u.rank}`}>
                      <Coins className="w-3.5 h-3.5" /> +{referralData.weekly_bonuses[String(u.rank)]}/wk
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 text-sm font-bold text-white w-24 justify-end">
                    <Gift className="w-3.5 h-3.5 text-emerald-400" /> {u.completed_referrals}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-4">
            <Tabs value={track} onValueChange={setTrack}>
              <TabsList className="bg-white/5 p-1">
                <TabsTrigger value="data_cloud" data-testid="leaderboard-track-data-cloud" className="data-[state=active]:bg-primary data-[state=active]:text-white">Data Cloud</TabsTrigger>
                <TabsTrigger value="sfmc" data-testid="leaderboard-track-sfmc" className="data-[state=active]:bg-teal-500 data-[state=active]:text-white">SFMC</TabsTrigger>
                <TabsTrigger value="mc_next" data-testid="leaderboard-track-mc-next" className="data-[state=active]:bg-fuchsia-500 data-[state=active]:text-white">MC Next</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-6">
            {track === "data_cloud" ? (
              <>
                <Tabs value={period} onValueChange={setPeriod}>
                  <TabsList className="bg-white/5 p-1">
                    <TabsTrigger value="all" data-testid="leaderboard-tab-global" className="data-[state=active]:bg-primary data-[state=active]:text-white">Global</TabsTrigger>
                    <TabsTrigger value="weekly" data-testid="leaderboard-tab-weekly" className="data-[state=active]:bg-primary data-[state=active]:text-white">Weekly</TabsTrigger>
                    <TabsTrigger value="monthly" data-testid="leaderboard-tab-monthly" className="data-[state=active]:bg-primary data-[state=active]:text-white">Monthly</TabsTrigger>
                  </TabsList>
                </Tabs>
                <form onSubmit={handleCountrySearch} className="flex items-center gap-2">
                  <input
                    value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Filter by country..."
                    data-testid="leaderboard-country-input"
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 w-44"
                  />
                  <button type="submit" data-testid="leaderboard-country-search-btn" className="bg-white/10 hover:bg-white/15 p-2 rounded-lg">
                    <Search className="w-4 h-4 text-slate-300" />
                  </button>
                </form>
              </>
            ) : track === "sfmc" ? (
              <p className="text-xs text-slate-400 mx-auto" data-testid="leaderboard-sfmc-hint">Ranked all-time by SFMC challenge days completed</p>
            ) : (
              <p className="text-xs text-slate-400 mx-auto" data-testid="leaderboard-mc-next-hint">Ranked all-time by MC Next challenge days completed</p>
            )}
          </div>

          {loading || !data ? (
            <div className="flex items-center justify-center py-20" data-testid="leaderboard-loading">
              <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="glass-panel rounded-2xl overflow-hidden">
              {data.leaderboard.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-10">No results for this filter yet.</p>
              )}
              {data.leaderboard.map((u) => (
                <div
                  key={u.user_id}
                  data-testid={`leaderboard-row-${u.rank}`}
                  className={`flex items-center gap-4 px-5 py-3.5 border-b border-white/5 last:border-0 ${u.is_me ? "bg-indigo-400/5" : ""}`}
                >
                  <div className="w-8 flex justify-center">
                    {u.rank <= 3 ? <Medal className={`w-5 h-5 ${MEDAL_COLORS[u.rank]}`} /> : <span className="text-sm font-bold text-slate-400">#{u.rank}</span>}
                  </div>
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-sm font-semibold text-white overflow-hidden">
                    {u.picture ? <img src={u.picture} alt={u.name} loading="lazy" className="w-full h-full object-cover" /> : u.name?.[0]}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{u.name} {u.is_me && <span className="text-indigo-400 text-xs">(You)</span>}</p>
                    <p className="text-xs text-slate-400">{u.days_completed} days completed{u.country ? ` · ${u.country}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-indigo-400 text-xs">
                    <Flame className="w-3.5 h-3.5" /> {u.streak_current}
                  </div>
                  <div className="text-sm font-bold text-[#FBBF24] w-20 text-right">{u.xp} XP</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
